// src/components/brands.js
// Marques — /brands. Cards recomposed: quiet ghost actions, mailboxes as
// rows with a star VIP toggle and hover-remove, tracking API rendered
// exception-based and data-driven (gaps in amber, all-good = one quiet line
// with a disclosure), copy-embed with React state instead of DOM queries.
// Unified sectioned create/edit modal; email picker modal kept, restyled.
// API: /api/brands/* (list, create, update, delete, associate-email,
// remove-email-association, set-vip-email), /api/tickets/embed.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaSearch, FaPlus, FaPen, FaTrash, FaTimes, FaSpinner, FaEnvelope,
    FaStar, FaRegStar, FaCheck, FaCheckCircle, FaTimesCircle, FaCode,
    FaExclamationTriangle, FaChevronDown, FaChevronRight, FaTags
} from 'react-icons/fa';
import './brands.css';

const emptyForm = () => ({
    name: '',
    description: '',
    support_page_url: '',
    tracking_api_config: {
        enabled: true,
        orders_get_url: '',
        orders_tracking_url: '',
        stock_url: '',
        api_key: '',
        parts_api_url: ''
    }
});

// KI-218: the spare-parts source (« URL des pièces détachées ») only exists for
// a brand whose access key Unanimity configured (or that already has a URL):
// the parts tools are offered to no other account.
const hasPartsSource = (config) => !!(config && (config.parts_api_url || config.has_parts_api_key));

const Brands = () => {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTerm = searchParams.get('q') || '';

    const [user, setUser] = useState(null);
    const [brands, setBrands] = useState(null); // null = loading
    const [userEmails, setUserEmails] = useState({ gmail_email: null, outlook_emails: [] });
    const [feedback, setFeedback] = useState(null);
    const feedbackTimer = useRef(null);

    // modal = {editing: brand|null, form, error, saving}
    const [modal, setModal] = useState(null);
    // deleteModal = {brand, deleting, error}
    const [deleteModal, setDeleteModal] = useState(null);
    // emailModal = {brandId, pendingEmail, vip, loading, error}
    const [emailModal, setEmailModal] = useState(null);
    const [expandedApi, setExpandedApi] = useState({});
    const [copiedBrandId, setCopiedBrandId] = useState(null);
    const copyTimer = useRef(null);

    const showFeedback = useCallback((type, message) => {
        clearTimeout(feedbackTimer.current);
        setFeedback({ type, message });
        if (type === 'success') {
            feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
        }
    }, []);

    useEffect(() => () => { clearTimeout(feedbackTimer.current); clearTimeout(copyTimer.current); }, []);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

    const fetchBrands = useCallback(async () => {
        try {
            const response = await axios.get('/api/brands/list', { headers: authHeaders() });
            setBrands(response.data || []);
        } catch (error) {
            console.error('Failed to fetch brands:', error);
            setBrands([]);
            showFeedback('error', t('brandsPage.fetchError', 'Impossible de charger les marques.'));
        }
    }, [showFeedback, t]);

    useEffect(() => {
        const headers = authHeaders();
        axios.get('/api/users/user', { headers })
            .then((res) => setUser(res.data))
            .catch((err) => console.error('Failed to fetch user:', err));
        axios.get('/api/users/user/emails', { headers })
            .then((res) => setUserEmails(res.data || { gmail_email: null, outlook_emails: [] }))
            .catch((err) => console.error('Failed to fetch user emails:', err));
        fetchBrands();
    }, [fetchBrands]);

    const canManage = user && ['admin', 'seller', 'eshop'].includes(user.role);

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString(i18n.language || 'fr', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    // Emails of a brand, normalized over both storage shapes
    const brandEmails = (brand) => {
        if (brand.associated_emails && brand.associated_emails.length > 0) return brand.associated_emails;
        if (brand.associated_email) return [{ email: brand.associated_email, is_vip: false, legacy: true }];
        return [];
    };

    // Tracking API summary — data-driven so new config fields can't be
    // silently dropped; the only actionable gap is a missing API key.
    const API_FIELDS = [
        { key: 'orders_get_url', label: t('brandsPage.apiOrdersUrl', 'URL des commandes') },
        { key: 'orders_tracking_url', label: t('brandsPage.apiTrackingUrl', 'URL de suivi') },
        { key: 'stock_url', label: t('brandsPage.apiStockUrl', 'URL du stock') },
        { key: 'has_api_key', label: t('brandsPage.apiKey', 'Clé API'), isKey: true },
        { key: 'parts_api_url', label: t('brandsPage.apiPartsUrl', 'URL des pièces détachées'), shownIf: hasPartsSource },
    ];
    const apiFieldsOf = (config) => API_FIELDS.filter((f) => !f.shownIf || f.shownIf(config));

    // ------------------------------------------------------------ CRUD

    const openModal = (brand = null) => {
        if (!canManage) return;
        setModal({
            editing: brand,
            form: brand ? {
                name: brand.name,
                description: brand.description || '',
                support_page_url: brand.support_page_url || '',
                tracking_api_config: {
                    enabled: brand.tracking_api_config?.enabled !== false,
                    orders_get_url: brand.tracking_api_config?.orders_get_url || '',
                    orders_tracking_url: brand.tracking_api_config?.orders_tracking_url || '',
                    stock_url: brand.tracking_api_config?.stock_url || '',
                    api_key: '', // never pre-filled
                    parts_api_url: brand.tracking_api_config?.parts_api_url || ''
                }
            } : emptyForm(),
            error: null,
            saving: false,
        });
    };

    const setForm = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));
    const setApiForm = (patch) => setModal((m) => ({
        ...m,
        form: { ...m.form, tracking_api_config: { ...m.form.tracking_api_config, ...patch } },
        error: null,
    }));

    const submitModal = async () => {
        const { form, editing } = modal;
        if (!form.name.trim() || !form.description.trim()) {
            setModal((m) => ({ ...m, error: t('brandsPage.requiredFields', 'Le nom et la description sont requis.') }));
            return;
        }
        // Always sent: '' clears the support page (KI-218), the backend checks it is https.
        const data = {
            name: form.name.trim(),
            description: form.description.trim(),
            support_page_url: (form.support_page_url || '').trim(),
        };
        const config = form.tracking_api_config;
        const showParts = hasPartsSource(editing?.tracking_api_config);
        if (config.orders_get_url || config.orders_tracking_url || config.stock_url || config.api_key || showParts) {
            data.tracking_api_config = { enabled: config.enabled !== false };
            if (config.orders_get_url) data.tracking_api_config.orders_get_url = config.orders_get_url;
            if (config.orders_tracking_url) data.tracking_api_config.orders_tracking_url = config.orders_tracking_url;
            // Always sent: the backend keeps an omitted stock URL, '' clears it (KI-215).
            data.tracking_api_config.stock_url = (config.stock_url || '').trim();
            if (config.api_key) data.tracking_api_config.api_key = config.api_key;
            // Sent when the field is shown: '' clears it; omitted, the backend keeps it (KI-218).
            if (showParts) data.tracking_api_config.parts_api_url = (config.parts_api_url || '').trim();
        }
        setModal((m) => ({ ...m, saving: true, error: null }));
        try {
            if (editing) {
                await axios.put(`/api/brands/update/${editing.id}`, data, { headers: authHeaders() });
                showFeedback('success', t('brandsPage.updated', 'Marque mise à jour.'));
            } else {
                await axios.post('/api/brands/create', data, { headers: authHeaders() });
                showFeedback('success', t('brandsPage.created', 'Marque créée.'));
            }
            setModal(null);
            fetchBrands();
        } catch (error) {
            console.error('Failed to save brand:', error);
            setModal((m) => ({
                ...m, saving: false,
                error: error.response?.data?.detail || t('brandsPage.saveError', 'Échec de l’enregistrement de la marque.'),
            }));
        }
    };

    const confirmDelete = async () => {
        setDeleteModal((m) => ({ ...m, deleting: true, error: null }));
        try {
            await axios.delete(`/api/brands/delete/${deleteModal.brand.id}`, { headers: authHeaders() });
            setDeleteModal(null);
            showFeedback('success', t('brandsPage.deleted', 'Marque supprimée.'));
            fetchBrands();
        } catch (error) {
            console.error('Failed to delete brand:', error);
            setDeleteModal((m) => ({
                ...m, deleting: false,
                error: error.response?.data?.detail || t('brandsPage.deleteError', 'Échec de la suppression de la marque.'),
            }));
        }
    };

    // ------------------------------------------------------------ emails

    const associateEmail = async (brandId, email, isVip) => {
        setEmailModal((m) => ({ ...m, loading: true, error: null }));
        try {
            await axios.post('/api/brands/associate-email',
                { brand_id: brandId, email, is_vip: isVip },
                { headers: authHeaders() });
            setEmailModal((m) => ({ ...m, loading: false, pendingEmail: null, vip: false }));
            showFeedback('success', t('brandsPage.emailAssociated', 'Boîte mail associée.'));
            fetchBrands();
        } catch (error) {
            console.error('Failed to associate email:', error);
            setEmailModal((m) => ({
                ...m, loading: false,
                error: error.response?.data?.detail || t('brandsPage.emailAssociateError', 'Échec de l’association de la boîte mail.'),
            }));
        }
    };

    const removeEmail = async (brandId, email) => {
        try {
            await axios.post('/api/brands/remove-email-association',
                { brand_id: brandId, email },
                { headers: authHeaders() });
            showFeedback('success', t('brandsPage.emailRemoved', 'Association supprimée.'));
            fetchBrands();
        } catch (error) {
            console.error('Failed to remove email association:', error);
            showFeedback('error', error.response?.data?.detail || t('brandsPage.emailRemoveError', 'Échec de la suppression de l’association.'));
        }
    };

    const toggleVip = async (brandId, email, currentVip) => {
        try {
            await axios.post('/api/brands/set-vip-email',
                { brand_id: brandId, email, is_vip: !currentVip },
                { headers: authHeaders() });
            fetchBrands();
        } catch (error) {
            console.error('Failed to toggle VIP:', error);
            showFeedback('error', error.response?.data?.detail || t('brandsPage.vipError', 'Échec du changement de statut VIP.'));
        }
    };

    // ------------------------------------------------------------ embed copy

    const copyTicketEmbed = async (brandId) => {
        try {
            const params = brandId ? `?brand_id=${brandId}` : '';
            const response = await axios.get(`/api/tickets/embed${params}`, { headers: authHeaders() });
            const code = response.data?.embed_code || response.data;
            await navigator.clipboard.writeText(typeof code === 'string' ? code : JSON.stringify(code));
            clearTimeout(copyTimer.current);
            setCopiedBrandId(brandId);
            copyTimer.current = setTimeout(() => setCopiedBrandId(null), 2500);
        } catch (error) {
            console.error('Failed to copy embed code:', error);
            showFeedback('error', t('brandsPage.embedError', 'Impossible de copier le code du formulaire.'));
        }
    };

    // ------------------------------------------------------------ render

    const loading = brands === null;
    const list = brands || [];
    const q = searchTerm.toLowerCase();
    const filtered = q
        ? list.filter((brand) =>
            (brand.name || '').toLowerCase().includes(q) ||
            (brand.description || '').toLowerCase().includes(q))
        : list;

    const emailModalBrand = emailModal ? list.find((b) => b.id === emailModal.brandId) : null;
    const availableEmails = emailModalBrand
        ? [userEmails.gmail_email, ...(userEmails.outlook_emails || [])]
            .filter((email) => email && !brandEmails(emailModalBrand).some((e) => e.email === email))
        : [];

    return (
        <div className="brands-page">
            {/* Header */}
            <div className="bd-header">
                <div className="bd-heading">
                    <h1>{t('brandsPage.title', 'Marques')}</h1>
                    <p>{t('brandsPage.subtitle', 'Chaque marque relie ses boîtes mail entrantes, son API de commandes et son formulaire de ticket.')}</p>
                </div>
                <div className="bd-header-actions">
                    <div className="bd-search">
                        <FaSearch aria-hidden="true" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                const next = new URLSearchParams(searchParams);
                                if (e.target.value) next.set('q', e.target.value); else next.delete('q');
                                setSearchParams(next, { replace: true });
                            }}
                            placeholder={t('brandsPage.searchPlaceholder', 'Rechercher une marque…')}
                        />
                    </div>
                    {canManage && (
                        <button type="button" className="u-btn u-btn--primary" onClick={() => openModal()}>
                            <FaPlus /> {t('brandsPage.create', 'Créer une marque')}
                        </button>
                    )}
                </div>
            </div>

            {feedback && (
                <div className={`bd-feedback ${feedback.type}`} role="status">
                    {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                    {feedback.message}
                </div>
            )}

            {loading ? (
                <div className="bd-skeleton" aria-hidden="true">
                    {Array.from({ length: 2 }, (_, i) => <div key={i} className="bd-skeleton-card" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="bd-empty">
                    <FaTags className="bd-empty-icon" />
                    <p>
                        {list.length === 0
                            ? t('brandsPage.noBrands', 'Aucune marque pour le moment. Créez-en une pour segmenter vos boîtes mail et vos formulaires.')
                            : t('brandsPage.noMatches', 'Aucune marque ne correspond à la recherche.')}
                    </p>
                </div>
            ) : (
                <div className="bd-grid">
                    {filtered.map((brand) => {
                        const emails = brandEmails(brand);
                        const config = brand.tracking_api_config;
                        const configuredFields = config
                            ? API_FIELDS.filter((f) => (f.isKey ? config.has_api_key : config[f.key]))
                            : [];
                        const missingKey = config && !config.has_api_key;
                        const apiOpen = !!expandedApi[brand.id];
                        const created = formatDate(brand.created_at);
                        const updated = formatDate(brand.updated_at);
                        return (
                            <div key={brand.id} className="bd-card">
                                <div className="bd-card-header">
                                    <h2 title={brand.name}>{brand.name}</h2>
                                    {canManage && (
                                        <div className="bd-card-actions">
                                            <button type="button" className="bd-icon-btn" onClick={() => openModal(brand)} title={t('brandsPage.edit', 'Modifier')} aria-label={t('brandsPage.edit', 'Modifier')}>
                                                <FaPen />
                                            </button>
                                            <button type="button" className="bd-icon-btn danger" onClick={() => setDeleteModal({ brand, deleting: false, error: null })} title={t('brandsPage.delete', 'Supprimer')} aria-label={t('brandsPage.delete', 'Supprimer')}>
                                                <FaTrash />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {brand.description && <p className="bd-card-desc">{brand.description}</p>}

                                {/* Mailboxes */}
                                <div className="bd-section">
                                    <h3>{t('brandsPage.mailboxes', 'Boîtes mail associées')}</h3>
                                    {emails.length === 0 ? (
                                        <p className="bd-muted-text">
                                            {t('brandsPage.noMailboxes', 'Aucune boîte mail — les courriels entrants ne sont pas rattachés à cette marque.')}
                                        </p>
                                    ) : (
                                        <ul className="bd-email-list">
                                            {emails.map((emailObj) => (
                                                <li key={emailObj.email} className="bd-email-row">
                                                    <FaEnvelope className="bd-email-icon" aria-hidden="true" />
                                                    <span className="bd-email-text" title={emailObj.email}>{emailObj.email}</span>
                                                    {canManage && !emailObj.legacy ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className={`bd-vip-btn ${emailObj.is_vip ? 'on' : ''}`}
                                                                onClick={() => toggleVip(brand.id, emailObj.email, emailObj.is_vip)}
                                                                title={emailObj.is_vip
                                                                    ? t('brandsPage.removeVip', 'Retirer le statut VIP')
                                                                    : t('brandsPage.makeVip', 'Marquer VIP')}
                                                                aria-label={emailObj.is_vip
                                                                    ? t('brandsPage.removeVip', 'Retirer le statut VIP')
                                                                    : t('brandsPage.makeVip', 'Marquer VIP')}
                                                            >
                                                                {emailObj.is_vip ? <FaStar /> : <FaRegStar />}
                                                                {emailObj.is_vip && <span>VIP</span>}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="bd-email-remove"
                                                                onClick={() => removeEmail(brand.id, emailObj.email)}
                                                                title={t('brandsPage.removeEmail', 'Retirer cette boîte mail')}
                                                                aria-label={t('brandsPage.removeEmail', 'Retirer cette boîte mail')}
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </>
                                                    ) : emailObj.is_vip && (
                                                        <span className="bd-vip-btn on static"><FaStar /> <span>VIP</span></span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {canManage && (
                                        <button
                                            type="button"
                                            className="bd-add-btn"
                                            onClick={() => setEmailModal({ brandId: brand.id, pendingEmail: null, vip: false, loading: false, error: null })}
                                        >
                                            <FaPlus /> {t('brandsPage.associateEmail', 'Associer une boîte mail')}
                                        </button>
                                    )}
                                </div>

                                {/* Tracking API — exceptions first */}
                                <div className="bd-section">
                                    <h3>{t('brandsPage.trackingApi', 'API de suivi')}</h3>
                                    {!config ? (
                                        <p className="bd-muted-text">{t('brandsPage.noApi', 'Non configurée.')}</p>
                                    ) : config.enabled === false ? (
                                        <p className="bd-muted-text">
                                            {t('brandsPage.apiDisabled', 'Désactivée — suivi via Ship24 uniquement.')}
                                        </p>
                                    ) : (
                                        <>
                                            {missingKey ? (
                                                <p className="bd-api-warn">
                                                    <FaExclamationTriangle /> {t('brandsPage.apiKeyMissing', 'Clé API manquante — les appels de suivi échoueront.')}
                                                </p>
                                            ) : (
                                                <p className="bd-api-ok">
                                                    <FaCheck /> {t('brandsPage.apiConfigured', 'Configurée ({{total}} champs)', { total: configuredFields.length })}
                                                </p>
                                            )}
                                            <button
                                                type="button"
                                                className="bd-disclosure"
                                                aria-expanded={apiOpen}
                                                onClick={() => setExpandedApi((prev) => ({ ...prev, [brand.id]: !prev[brand.id] }))}
                                            >
                                                {apiOpen ? <FaChevronDown /> : <FaChevronRight />}
                                                {t('brandsPage.apiDetails', 'Détails')}
                                            </button>
                                            {apiOpen && (
                                                <ul className="bd-api-list">
                                                    {apiFieldsOf(config).map((field) => {
                                                        const value = field.isKey ? config.has_api_key : config[field.key];
                                                        return (
                                                            <li key={field.key}>
                                                                <span className="bd-api-label">{field.label}</span>
                                                                {value ? (
                                                                    <span className="bd-api-value" title={typeof value === 'string' ? value : ''}>
                                                                        {field.isKey ? t('brandsPage.apiKeySet', 'renseignée') : value}
                                                                    </span>
                                                                ) : (
                                                                    <span className="bd-muted-dash">—</span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="bd-card-footer">
                                    <span className="bd-card-meta">
                                        {[
                                            created && t('brandsPage.metaCreated', 'Créée le {{date}}', { date: created }),
                                            updated && updated !== created && t('brandsPage.metaUpdated', 'modifiée le {{date}}', { date: updated }),
                                        ].filter(Boolean).join(' · ')}
                                    </span>
                                    {canManage && (
                                        <button
                                            type="button"
                                            className={`bd-embed-btn ${copiedBrandId === brand.id ? 'copied' : ''}`}
                                            onClick={() => copyTicketEmbed(brand.id)}
                                        >
                                            {copiedBrandId === brand.id
                                                ? (<><FaCheck /> {t('brandsPage.embedCopied', 'Copié')}</>)
                                                : (<><FaCode /> {t('brandsPage.copyEmbed', 'Copier le formulaire de ticket')}</>)}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create / edit modal */}
            {modal && (
                <div className="bd-modal-backdrop" onClick={() => setModal(null)}>
                    <div className="bd-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="bd-modal-header">
                            <h3>{modal.editing ? t('brandsPage.editTitle', 'Modifier la marque') : t('brandsPage.createTitle', 'Créer une marque')}</h3>
                            <button type="button" className="bd-icon-btn" onClick={() => setModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="bd-modal-body">
                            <div className="bd-form-section">
                                <h4>{t('brandsPage.sectionIdentity', 'Identité')}</h4>
                                <div className="bd-field">
                                    <label htmlFor="bd-name">{t('brandsPage.name', 'Nom')} *</label>
                                    <input
                                        id="bd-name"
                                        type="text"
                                        maxLength={100}
                                        value={modal.form.name}
                                        onChange={(e) => setForm({ name: e.target.value })}
                                        autoFocus
                                    />
                                </div>
                                <div className="bd-field">
                                    <label htmlFor="bd-desc">{t('brandsPage.description', 'Description')} *</label>
                                    <textarea
                                        id="bd-desc"
                                        rows={3}
                                        maxLength={500}
                                        value={modal.form.description}
                                        onChange={(e) => setForm({ description: e.target.value })}
                                        placeholder={t('brandsPage.descriptionPlaceholder', 'Utilisée par l’IA pour identifier la marque…')}
                                    />
                                </div>
                            </div>
                            <div className="bd-form-section">
                                <h4>{t('brandsPage.sectionApi', 'API de suivi')}</h4>
                                <p className="bd-field-hint">
                                    {t('brandsPage.sectionApiHint', 'Optionnelle — permet à l’IA de consulter commandes et suivis de cette marque.')}
                                </p>
                                <label className="bd-vip-check">
                                    <input
                                        type="checkbox"
                                        checked={modal.form.tracking_api_config.enabled !== false}
                                        onChange={(e) => setApiForm({ enabled: e.target.checked })}
                                    />
                                    {t('brandsPage.apiEnabled', 'API interne activée (décochée : suivi via Ship24 uniquement)')}
                                </label>
                                <div className="bd-field">
                                    <label htmlFor="bd-orders-url">{t('brandsPage.apiOrdersUrl', 'URL des commandes')}</label>
                                    <input
                                        id="bd-orders-url"
                                        type="url"
                                        value={modal.form.tracking_api_config.orders_get_url}
                                        onChange={(e) => setApiForm({ orders_get_url: e.target.value })}
                                        placeholder="https://api.exemple.com/orders/{num_commande}"
                                    />
                                </div>
                                <div className="bd-field">
                                    <label htmlFor="bd-tracking-url">{t('brandsPage.apiTrackingUrl', 'URL de suivi')}</label>
                                    <input
                                        id="bd-tracking-url"
                                        type="url"
                                        value={modal.form.tracking_api_config.orders_tracking_url}
                                        onChange={(e) => setApiForm({ orders_tracking_url: e.target.value })}
                                        placeholder="https://api.exemple.com/tracking"
                                    />
                                </div>
                                <div className="bd-field">
                                    <label htmlFor="bd-stock-url">{t('brandsPage.apiStockUrl', 'URL du stock')}</label>
                                    <input
                                        id="bd-stock-url"
                                        type="url"
                                        value={modal.form.tracking_api_config.stock_url}
                                        onChange={(e) => setApiForm({ stock_url: e.target.value })}
                                        placeholder="https://api.exemple.com/skus/stock"
                                    />
                                    <p className="bd-field-hint">
                                        {t('brandsPage.apiStockUrlHint', 'Optionnelle — délai de disponibilité des articles d’une commande, consulté par l’agent IA avec la clé API ci-dessous.')}
                                    </p>
                                </div>
                                <div className="bd-field">
                                    <label htmlFor="bd-api-key">{t('brandsPage.apiKey', 'Clé API')}</label>
                                    <input
                                        id="bd-api-key"
                                        type="password"
                                        autoComplete="new-password"
                                        value={modal.form.tracking_api_config.api_key}
                                        onChange={(e) => setApiForm({ api_key: e.target.value })}
                                        placeholder={modal.editing?.tracking_api_config?.has_api_key
                                            ? t('brandsPage.apiKeyKeep', 'Laisser vide pour conserver la clé actuelle')
                                            : t('brandsPage.apiKeyPlaceholder', 'Jeton d’authentification')}
                                    />
                                </div>
                                {hasPartsSource(modal.editing?.tracking_api_config) && (
                                    <div className="bd-field">
                                        <label htmlFor="bd-parts-url">{t('brandsPage.apiPartsUrl', 'URL des pièces détachées')}</label>
                                        <input
                                            id="bd-parts-url"
                                            type="url"
                                            value={modal.form.tracking_api_config.parts_api_url}
                                            onChange={(e) => setApiForm({ parts_api_url: e.target.value })}
                                            placeholder="https://api.exemple.com/parts"
                                        />
                                        <p className="bd-field-hint">
                                            {t('brandsPage.apiPartsUrlHint', 'Catalogue de pièces détachées consulté par l’agent IA (disponibilité, compatibilité).')}
                                            {' '}
                                            {modal.editing.tracking_api_config.has_parts_api_key
                                                ? t('brandsPage.apiPartsKeySet', 'Clé d’accès configurée par Unanimity.')
                                                : t('brandsPage.apiPartsKeyMissing', 'Aucune clé d’accès configurée — les outils pièces restent désactivés. Contactez Unanimity.')}
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="bd-form-section">
                                <h4>{t('brandsPage.sectionSupport', 'Support client')}</h4>
                                <div className="bd-field">
                                    <label htmlFor="bd-support-url">{t('brandsPage.supportPageUrl', 'Page support')}</label>
                                    <input
                                        id="bd-support-url"
                                        type="url"
                                        maxLength={2048}
                                        value={modal.form.support_page_url}
                                        onChange={(e) => setForm({ support_page_url: e.target.value })}
                                        placeholder="https://www.exemple.com/support"
                                    />
                                    <p className="bd-field-hint">
                                        {t('brandsPage.supportPageUrlHint', 'Page de votre site où le formulaire de ticket est intégré : les e-mails de notification et le chatbot y renvoient le client. Vide : aucun lien n’est envoyé.')}
                                    </p>
                                </div>
                            </div>
                            {modal.error && <div className="bd-modal-error">{modal.error}</div>}
                        </div>
                        <div className="bd-modal-footer">
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setModal(null)} disabled={modal.saving}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="u-btn u-btn--primary" onClick={submitModal} disabled={modal.saving}>
                                {modal.saving
                                    ? (<><FaSpinner className="bd-spin" /> {t('common.saving', 'Enregistrement…')}</>)
                                    : modal.editing ? t('common.save', 'Enregistrer') : t('brandsPage.submitCreate', 'Créer la marque')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            {deleteModal && (
                <div className="bd-modal-backdrop" onClick={() => setDeleteModal(null)}>
                    <div className="bd-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="bd-modal-header">
                            <h3><FaExclamationTriangle className="bd-warn-icon" /> {t('brandsPage.deleteTitle', 'Supprimer la marque')}</h3>
                            <button type="button" className="bd-icon-btn" onClick={() => setDeleteModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="bd-modal-body">
                            <p className="bd-delete-text">
                                {t('brandsPage.deleteConfirm', 'Supprimer « {{name}} » ? Les associations de boîtes mail et la configuration API seront perdues.', { name: deleteModal.brand.name })}
                            </p>
                            {deleteModal.error && <div className="bd-modal-error">{deleteModal.error}</div>}
                        </div>
                        <div className="bd-modal-footer">
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setDeleteModal(null)} disabled={deleteModal.deleting}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="bd-danger-btn" onClick={confirmDelete} disabled={deleteModal.deleting}>
                                {deleteModal.deleting
                                    ? (<><FaSpinner className="bd-spin" /> {t('brandsPage.deleting', 'Suppression…')}</>)
                                    : (<><FaTrash /> {t('brandsPage.delete', 'Supprimer')}</>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Email picker modal */}
            {emailModal && emailModalBrand && (
                <div className="bd-modal-backdrop" onClick={() => setEmailModal(null)}>
                    <div className="bd-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="bd-modal-header">
                            <h3>{t('brandsPage.emailModalTitle', 'Boîtes mail — {{name}}', { name: emailModalBrand.name })}</h3>
                            <button type="button" className="bd-icon-btn" onClick={() => setEmailModal(null)} aria-label={t('common.cancel', 'Fermer')}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="bd-modal-body">
                            {emailModal.pendingEmail ? (
                                <div className="bd-form-section">
                                    <h4>{t('brandsPage.addingEmail', 'Associer')}</h4>
                                    <p className="bd-pending-email">{emailModal.pendingEmail}</p>
                                    <label className="bd-vip-check">
                                        <input
                                            type="checkbox"
                                            checked={emailModal.vip}
                                            onChange={(e) => setEmailModal((m) => ({ ...m, vip: e.target.checked }))}
                                        />
                                        <FaStar aria-hidden="true" />
                                        {t('brandsPage.markAsVip', 'Marquer comme adresse VIP')}
                                    </label>
                                    <div className="bd-inline-actions">
                                        <button type="button" className="u-btn u-btn--secondary" onClick={() => setEmailModal((m) => ({ ...m, pendingEmail: null, vip: false }))} disabled={emailModal.loading}>
                                            {t('common.cancel', 'Annuler')}
                                        </button>
                                        <button type="button" className="u-btn u-btn--primary" onClick={() => associateEmail(emailModal.brandId, emailModal.pendingEmail, emailModal.vip)} disabled={emailModal.loading}>
                                            {emailModal.loading
                                                ? (<><FaSpinner className="bd-spin" /> {t('brandsPage.adding', 'Association…')}</>)
                                                : (<><FaPlus /> {t('brandsPage.addEmail', 'Associer')}</>)}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bd-form-section">
                                    <h4>{t('brandsPage.availableEmails', 'Boîtes connectées disponibles')}</h4>
                                    {availableEmails.length === 0 ? (
                                        <p className="bd-muted-text">
                                            {t('brandsPage.noMoreEmails', 'Toutes vos boîtes connectées sont déjà associées à cette marque.')}
                                        </p>
                                    ) : (
                                        <div className="bd-email-options">
                                            {availableEmails.map((email) => (
                                                <button
                                                    key={email}
                                                    type="button"
                                                    className="bd-email-option"
                                                    onClick={() => setEmailModal((m) => ({ ...m, pendingEmail: email }))}
                                                    disabled={emailModal.loading}
                                                >
                                                    <FaEnvelope aria-hidden="true" /> {email}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {emailModal.error && <div className="bd-modal-error">{emailModal.error}</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Brands;
